/**
 * Service for managing authentication tokens with persistence
 */
class AuthTokenService {
  private readonly TOKEN_KEY = 'amira_auth_access_token';
  private accessToken: string | null = null;
  private listeners: Set<(token: string | null) => void> = new Set();

  constructor() {
    // Try to load the token from sessionStorage on initialization
    this.loadFromStorage();
  }

  /**
   * Set the access token and save to sessionStorage
   * @param token The access token to store
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
    if (token) {
        sessionStorage.setItem(this.TOKEN_KEY, token);
    } else {
      // Clear from sessionStorage if token is null
      this.clearAccessToken();
    }
    // Notify all listeners of the token change
    this.notifyListeners();
  }

  /**
   * Get the current access token
   * @returns The stored access token or null if not set
   */
  getAccessToken(): string | null {
    // If not in memory, try to load from storage
    if (!this.accessToken) {
      this.loadFromStorage();
    }
    return this.accessToken;
  }

  /**
   * Check if an access token is available
   * @returns True if an access token is set
   */
  hasAccessToken(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Clear the stored access token from memory and sessionStorage
   */
  clearAccessToken(): void {
    this.accessToken = null;
    sessionStorage.removeItem(this.TOKEN_KEY);
    this.notifyListeners();
  }

  /**
   * Load the token from sessionStorage
   * @private
   */
  private loadFromStorage(): void {
    const storedToken = sessionStorage.getItem(this.TOKEN_KEY);
    if (storedToken) {
      this.accessToken = storedToken;
      this.notifyListeners();
    }
  }

  /**
   * Subscribe to token changes
   * @param listener Callback function to be called when token changes
   * @returns Function to unsubscribe
   */
  subscribe(listener: (token: string | null) => void): () => void {
    this.listeners.add(listener);
    // Call immediately with current token
    listener(this.accessToken);
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of token changes
   * @private
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.accessToken));
  }
}

// Create singleton instance
export const authTokenService = new AuthTokenService();
export default authTokenService; 